import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '../lib/auth'
import { supabase } from '../lib/supabase'
import { Logo } from '../components/Logo'
import { Eye, EyeOff, Mail, X, KeyRound } from 'lucide-react'
import { ThemeToggle } from '../components/ThemeToggle'
import { VersionInfo } from '../components/VersionInfo'
import toast from 'react-hot-toast'

// Turnstile site key from environment
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || ''

// Declare Turnstile types
declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: {
        sitekey: string
        callback: (token: string) => void
        'error-callback'?: () => void
        'expired-callback'?: () => void
        theme?: 'light' | 'dark' | 'auto'
      }) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

export function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showResetPassword, setShowResetPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetLoading, setResetLoading] = useState(false)

  // Turnstile CAPTCHA state
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const turnstileRef = useRef<HTMLDivElement>(null)
  const turnstileWidgetId = useRef<string | null>(null)

  // Password update state (after clicking reset link or invite link)
  // Check URL hash SYNCHRONOUSLY on initial render to catch invite/recovery before redirect
  const getInitialFlowState = () => {
    const hash = window.location.hash
    if (hash.includes('type=invite')) return { isRecovery: true, isInvite: true, error: null }
    if (hash.includes('type=recovery')) return { isRecovery: true, isInvite: false, error: null }
    // Check for errors in hash (e.g., expired link)
    if (hash.includes('error=')) {
      const errorMatch = hash.match(/error_description=([^&]+)/)
      const errorDesc = errorMatch ? decodeURIComponent(errorMatch[1].replace(/\+/g, ' ')) : null
      // Translate common errors to Swedish
      let swedishError = 'Ett fel uppstod'
      if (errorDesc?.includes('expired') || errorDesc?.includes('invalid')) {
        swedishError = 'Länken har gått ut eller är ogiltig. Begär en ny återställningslänk.'
      }
      return { isRecovery: false, isInvite: false, error: swedishError }
    }
    return { isRecovery: false, isInvite: false, error: null }
  }
  const initialFlow = getInitialFlowState()
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(initialFlow.isRecovery)
  const [isInviteFlow, setIsInviteFlow] = useState(initialFlow.isInvite)
  const [linkError] = useState<string | null>(initialFlow.error)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [updatePasswordLoading, setUpdatePasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)

  const { signIn, resetPassword, user, profile } = useAuth()
  const navigate = useNavigate()

  // Load Turnstile script and initialize widget
  useEffect(() => {
    // Skip if no site key or in password recovery mode
    if (!TURNSTILE_SITE_KEY || isPasswordRecovery) return

    // Load script if not already loaded
    const existingScript = document.querySelector('script[src*="turnstile"]')
    if (!existingScript) {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    }

    // Initialize widget when script is loaded
    const initTurnstile = () => {
      if (window.turnstile && turnstileRef.current && !turnstileWidgetId.current) {
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            setCaptchaToken(token)
          },
          'error-callback': () => {
            setCaptchaToken(null)
            toast.error('CAPTCHA-fel. Försök igen.')
          },
          'expired-callback': () => {
            setCaptchaToken(null)
          },
          theme: 'auto'
        })
      }
    }

    // Check if script is already loaded
    if (window.turnstile) {
      initTurnstile()
    } else {
      // Wait for script to load
      const checkInterval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkInterval)
          initTurnstile()
        }
      }, 100)

      // Cleanup interval after 10 seconds
      setTimeout(() => clearInterval(checkInterval), 10000)
    }

    return () => {
      // Cleanup widget on unmount
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current)
        turnstileWidgetId.current = null
      }
    }
  }, [isPasswordRecovery])

  // Reset Turnstile after failed login
  const resetTurnstile = useCallback(() => {
    setCaptchaToken(null)
    if (turnstileWidgetId.current && window.turnstile) {
      window.turnstile.reset(turnstileWidgetId.current)
    }
  }, [])

  // Clear error hash from URL if present
  useEffect(() => {
    if (linkError && window.location.hash.includes('error=')) {
      window.history.replaceState(null, '', window.location.pathname)
    }
  }, [linkError])

  // Check for password recovery event from Supabase
  useEffect(() => {
    // DON'T clear the hash - Supabase needs it to establish the session!
    // Supabase will clear it automatically after processing.

    // Listen for auth state changes - Supabase will process the hash automatically
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true)
      }
      // If we detected invite flow from URL but session comes later via SIGNED_IN
      if (event === 'SIGNED_IN' && initialFlow.isRecovery && session) {
        // Clear the hash now that session is established
        window.history.replaceState(null, '', window.location.pathname)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // Redirect to appropriate dashboard when user is authenticated
  useEffect(() => {
    // Don't redirect if in password recovery mode
    if (isPasswordRecovery) return

    // Also check URL hash directly - it might contain recovery/invite tokens
    const hash = window.location.hash
    if (hash.includes('type=recovery') || hash.includes('type=invite')) {
      setIsPasswordRecovery(true)
      return
    }

    if (user && profile) {
      const redirectPath = profile.role === 'admin' ? '/admin' : '/subcontractor'
      navigate(redirectPath, { replace: true })
    }
  }, [user, profile, navigate, isPasswordRecovery])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Check CAPTCHA if Turnstile is enabled
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      toast.error('Vänligen verifiera att du inte är en robot')
      return
    }

    setLoading(true)

    try {
      const { error } = await signIn(email, password, captchaToken || undefined)
      if (error) {
        toast.error('Inloggning misslyckades: ' + error.message)
        resetTurnstile()
      } else {
        toast.success('Inloggning lyckades!')
        // Redirect will be handled by auth state change
      }
    } catch (error) {
      toast.error('Ett fel uppstod. Försök igen.')
      resetTurnstile()
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!resetEmail) {
      toast.error('Vänligen ange din e-postadress')
      return
    }

    // Check CAPTCHA if Turnstile is enabled
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      toast.error('Vänligen verifiera att du inte är en robot')
      return
    }

    setResetLoading(true)

    try {
      const { error } = await resetPassword(resetEmail, captchaToken || undefined)
      if (error) {
        toast.error('Återställning misslyckades: ' + error.message)
        resetTurnstile()
      } else {
        toast.success('Återställningslänk har skickats till din e-postadress!')
        setShowResetPassword(false)
        setResetEmail('')
        resetTurnstile()
      }
    } catch (error) {
      toast.error('Ett fel uppstod. Försök igen.')
      resetTurnstile()
    } finally {
      setResetLoading(false)
    }
  }

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordError(null)

    // Validate password requirements
    const hasMinLength = newPassword.length >= 8
    const hasLowercase = /[a-z]/.test(newPassword)
    const hasUppercase = /[A-Z]/.test(newPassword)
    const hasDigit = /\d/.test(newPassword)
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'/`~]/.test(newPassword)

    if (!hasMinLength) {
      setPasswordError('Lösenordet måste vara minst 8 tecken')
      return
    }
    if (!hasLowercase || !hasUppercase) {
      setPasswordError('Lösenordet måste innehålla både stora och små bokstäver')
      return
    }
    if (!hasDigit) {
      setPasswordError('Lösenordet måste innehålla minst en siffra')
      return
    }
    if (!hasSpecialChar) {
      setPasswordError('Lösenordet måste innehålla minst ett specialtecken')
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Lösenorden matchar inte')
      return
    }

    setUpdatePasswordLoading(true)

    try {
      // Check if we have a valid session
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        setPasswordError('Sessionen har gått ut. Vänligen begär en ny återställningslänk.')
        setIsPasswordRecovery(false)
        return
      }

      const { error } = await supabase.auth.updateUser({ password: newPassword })

      if (error) {
        console.error('Update password error:', error)
        // Translate common Supabase error messages to Swedish
        let errorMessage = error.message
        if (error.message.includes('at least 8 characters')) {
          errorMessage = 'Lösenordet måste vara minst 8 tecken'
        } else if (error.message.includes('password')) {
          errorMessage = 'Kunde inte uppdatera lösenord. Kontrollera att det uppfyller alla krav.'
        }
        setPasswordError(errorMessage)
      } else {
        // Note: account_activated is set via database trigger or admin action
        // TG users don't have permission to update subcontractors table

        // Sign out FIRST to prevent redirect race condition
        await supabase.auth.signOut()
        // Then update state
        setNewPassword('')
        setConfirmPassword('')
        setIsPasswordRecovery(false)
        setIsInviteFlow(false)
        toast.success(isInviteFlow
          ? 'Ditt konto är aktiverat! Logga in med ditt nya lösenord.'
          : 'Lösenordet har uppdaterats! Logga in med ditt nya lösenord.')
      }
    } catch (error) {
      console.error('Unexpected error:', error)
      setPasswordError('Ett fel uppstod. Försök igen.')
    } finally {
      setUpdatePasswordLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-nature-200 dark:from-nature-900 dark:via-nature-800 dark:to-nature-700 dark:bg-gradient-to-br flex items-center justify-center pt-8 pb-28 sm:py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-200 relative overflow-hidden">
      {/* Theme Toggle - Top Right */}
      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle />
      </div>
      
      {/* Dark mode pattern overlay */}
      <div className="absolute inset-0 opacity-0 dark:opacity-10 transition-opacity duration-200">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M30 30c0-16.569 13.431-30 30-30v60c-16.569 0-30-13.431-30-30zm0 0c0 16.569-13.431 30-30 30V0c16.569 0 30 13.431 30 30z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px'
        }}></div>
      </div>
      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center bg-[#9e7e88] rounded-2xl p-6 w-fit">
            <Logo size="4xl" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-nature-900 dark:text-nature-100">
            {isPasswordRecovery
              ? (isInviteFlow ? 'Välkommen!' : 'Ange nytt lösenord')
              : 'Logga in'}
          </h2>
          {/* Link error message (expired/invalid link) */}
          {linkError && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{linkError}</p>
            </div>
          )}
          {isPasswordRecovery && (
            <p className="mt-2 text-sm text-nature-600 dark:text-nature-400">
              {isInviteFlow
                ? 'Skapa ett lösenord för att aktivera ditt konto'
                : 'Ange ditt nya lösenord nedan'}
            </p>
          )}
        </div>

        {/* Password Recovery Form */}
        {isPasswordRecovery ? (
          <form className="mt-8 space-y-6" onSubmit={handleUpdatePassword}>
            <div className="space-y-4">
              <div>
                <label htmlFor="new-password" className="form-label">
                  {isInviteFlow ? 'Välj lösenord' : 'Nytt lösenord'}
                </label>
                <div className="relative">
                  <input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value)
                      setPasswordError(null)
                    }}
                    className="input-field pr-10"
                    placeholder="Ange lösenord"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-nature-400 dark:text-nature-500" />
                    ) : (
                      <Eye className="h-5 w-5 text-nature-400 dark:text-nature-500" />
                    )}
                  </button>
                </div>
                <ul className="mt-1.5 text-xs text-nature-500 dark:text-nature-400 space-y-0.5">
                  <li>• Minst 8 tecken</li>
                  <li>• Stora och små bokstäver</li>
                  <li>• Minst en siffra</li>
                  <li>• Minst ett specialtecken (!@#$%^&* etc.)</li>
                </ul>
              </div>

              <div>
                <label htmlFor="confirm-password" className="form-label">
                  Bekräfta lösenord
                </label>
                <input
                  id="confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    setPasswordError(null)
                  }}
                  className="input-field"
                  placeholder="Upprepa lösenordet"
                />
              </div>
            </div>

            {/* Error message */}
            {passwordError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{passwordError}</p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={updatePasswordLoading}
                className="btn-primary w-full flex justify-center items-center py-3 px-4 text-sm font-medium"
              >
                {updatePasswordLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <KeyRound className="h-5 w-5 mr-2" />
                    {isInviteFlow ? 'Aktivera konto' : 'Uppdatera lösenord'}
                  </>
                )}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setIsPasswordRecovery(false)
                  setNewPassword('')
                  setConfirmPassword('')
                  setPasswordError(null)
                }}
                className="text-sm text-nature-600 hover:text-logo-olive dark:text-logo-lavender dark:hover:text-logo-olive font-medium transition-colors duration-200"
              >
                Tillbaka till inloggning
              </button>
            </div>
          </form>
        ) : (
          /* Login Form */
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="form-label">
                E-postadress
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                placeholder="ange@email.se"
              />
            </div>

            <div>
              <label htmlFor="password" className="form-label">
                Lösenord
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pr-10"
                  placeholder="Ange ditt lösenord"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-nature-400 dark:text-nature-500" />
                  ) : (
                    <Eye className="h-5 w-5 text-nature-400 dark:text-nature-500" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Turnstile CAPTCHA Widget */}
          {TURNSTILE_SITE_KEY && (
            <div className="flex justify-center">
              <div ref={turnstileRef} />
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading || (TURNSTILE_SITE_KEY && !captchaToken)}
              className="btn-primary w-full flex justify-center py-3 px-4 text-sm font-medium"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                'Logga in'
              )}
            </button>
          </div>

          <div className="text-center space-y-2">
            <button
              type="button"
              onClick={() => {
                setShowResetPassword(true)
                setResetEmail(email) // Pre-fill with login email
              }}
              className="text-sm text-nature-600 hover:text-logo-olive dark:text-logo-lavender dark:hover:text-logo-olive font-medium transition-colors duration-200"
            >
              Glömt lösenord?
            </button>
            <div>
              <a
                href="mailto:info@tradgardsupport.se"
                className="text-nature-600 hover:text-logo-olive dark:text-logo-lavender dark:hover:text-logo-olive text-sm font-medium flex items-center justify-center mx-auto transition-colors duration-200"
              >
                <Mail className="h-4 w-4 mr-2" />
                info@tradgardsupport.se
              </a>
            </div>
          </div>
        </form>
        )}

        {/* Password Reset Form - only show when not in password recovery mode */}
        {!isPasswordRecovery && showResetPassword && (
          <div className="mt-6 p-6 bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100 flex items-center">
                <Mail className="h-5 w-5 mr-2" />
                Återställ lösenord
              </h3>
              <button
                onClick={() => {
                  setShowResetPassword(false)
                  setResetEmail('')
                }}
                className="text-nature-400 hover:text-nature-600 dark:hover:text-nature-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label htmlFor="reset-email" className="form-label">
                  E-postadress
                </label>
                <input
                  id="reset-email"
                  type="email"
                  required
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="input-field"
                  placeholder="ange@email.se"
                  autoComplete="email"
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={resetLoading}
                  className="btn-primary flex-1 flex justify-center py-2 px-4 text-sm font-medium"
                >
                  {resetLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    'Skicka återställningslänk'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowResetPassword(false)
                    setResetEmail('')
                  }}
                  className="btn-secondary flex-1 py-2 px-4 text-sm font-medium"
                >
                  Avbryt
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Footer with version info and copyright */}
      <footer className="absolute bottom-0 left-0 right-0 border-t border-nature-200 dark:border-nature-700 bg-[#faf9f7] dark:bg-nature-800 transition-colors duration-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
            {/* Copyright */}
            <div className="text-sm text-nature-500 dark:text-nature-400">
              © {new Date().getFullYear()} MB Trädgård Support AB
            </div>
            
            {/* Version Info */}
            <div className="flex items-center space-x-4">
              <VersionInfo position="footer" />
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
